import { createContext } from 'react'
import { createClient } from '@tumelo/monolith-client/client'
import { institutionalMeetingsearchList, PaginatedMeetingList } from '@tumelo/monolith-client'
import { fetchAuthSession } from 'aws-amplify/auth'
import { monolithConfig } from './monolithEnvironment'
import { sampleMeetings } from './samples/monolithMeeting'

// Custom error class for Monolith API errors
export class MonolithApiError extends Error {
  readonly statusCode?: number

  constructor(message: string, opts: { statusCode?: number; cause?: unknown } = {}) {
    super(message, { cause: opts.cause })
    this.name = 'MonolithApiError'
    this.statusCode = opts.statusCode
  }
}

// Define the interface for our monolith client wrapper
export interface MonolithClient {
  listGeneralMeetings: (params: {
    schemeName: string
    organizationTitleSearch?: string
    cursor?: string
    pageSize?: number
    sortOrder?: 'asc' | 'desc'
    status?: 'all' | 'closed' | 'open'
    themeCodes?: string
  }) => Promise<PaginatedMeetingList>
}

// Mock client implementation
const MockMonolithClient: MonolithClient = {
  listGeneralMeetings: async () => ({
    results: [...sampleMeetings],
    next: 'http://localhost:8000/api/meetings/?cursor=cD0yMDI1LTA5LTEwKzA3JTNBMDA%3D',
    previous: 'http://localhost:8000/api/meetings/?cursor=prev123',
  }),
}

// Factory function to create the appropriate client
export const getMockedOrNotMockedMonolithClient = (config: monolithConfig): (() => Promise<MonolithClient>) => {
  if (config.clientType === 'mock') {
    return async () => MockMonolithClient
  }

  return async () => {
    const session = await fetchAuthSession()
    const token = session.tokens?.idToken?.toString()

    // Create a configured client instance
    const client = createClient({
      baseUrl: config.clientAddress, // Use the actual monolith API URL
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })

    // Return a wrapper that uses the generated functions
    return {
      listGeneralMeetings: async (params: {
        schemeName: string
        organizationTitleSearch?: string
        cursor?: string
        pageSize?: number
        sortOrder?: 'asc' | 'desc'
        status?: 'all' | 'closed' | 'open'
        themeCodes?: string
      }) => {
        const response = await institutionalMeetingsearchList({
          client,
          query: {
            scheme_name: params.schemeName,
            organization_title_search: params.organizationTitleSearch,
            cursor: params.cursor,
            page_size: params.pageSize,
            sort_order: params.sortOrder,
            status: params.status,
            theme_codes: params.themeCodes,
          },
        })

        if (response.error) {
          const errorDetails = JSON.stringify(response.error, null, 2)
          throw new MonolithApiError(`API Error: ${errorDetails}`, {
            statusCode: response.response?.status,
            cause: response.error,
          })
        }

        return response.data!
      },
    }
  }
}

// Context for dependency injection
export const MonolithClientContext = createContext<() => Promise<MonolithClient>>(() => {
  throw new Error('need to set monolith client context value')
})
